/**
 * Shared test fixtures for the Edit User page, shaped like what
 * Management::UserDetail actually sends.
 *
 * A plain module rather than an export from one of the test files: importing a test
 * file into another makes its suite run twice.
 */

export const OPTIONS = {
  jobRoles: [
    { label: 'Fleet Manager', value: 'fleet_manager' },
    { label: 'Technician', value: 'technician' },
  ],
  countries: [
    { label: 'United States', value: 'US' },
    { label: 'Canada', value: 'CA' },
    { label: 'Puerto Rico', value: 'PR' },
  ],
  states: {
    // Puerto Rico genuinely has no subdivisions in GeoHashes.
    US: [{ label: 'Alabama', value: 'AL' }, { label: 'Texas', value: 'TX' }],
    CA: [{ label: 'Ontario', value: 'ON' }],
    PR: [],
  },
  phoneCountryCodes: [{ label: '+1', value: 1 }, { label: '+52', value: 52 }],
};

/**
 * The role menu, shaped exactly like Management::UserFormOptions#permissions —
 * two single roles and one group whose choices are mutually exclusive. The
 * design draws the group as two columns: ASSET MGMT. turns it on, MGMT. ROLE
 * picks the tier.
 */
export const PERMISSION_OPTIONS = [
  {
    id: 'fleet-administrator',
    label: 'Fleet Administrator',
    roleId: 'role-fleet-admin',
  },
  { id: 'report-user', label: 'Report User', roleId: 'role-report' },
  {
    id: 'management-role',
    label: 'Management Role',
    choices: [
      { label: 'Member', roleId: 'role-member' },
      { label: 'Manager', roleId: 'role-manager' },
    ],
  },
];

export const accountFixture = (overrides = {}) => ({
  accountId: 4711,
  name: 'Account Name A',
  roleIds: ['role-fleet-admin'],
  discoverability: {
    visibleAsExternalRecipient: true,
    visibleAsInternalRecipient: true,
  },
  revoked: false,
  // What the matrix has no column for, named by the server so the header can
  // still tag it. Empty by default: the fixture's roles are all on the matrix.
  unlistedRoleNames: {},
  ...overrides,
});

/**
 * Management::UserAccessPermissions' payload. Teams do not exist yet (PD-4213),
 * so the server sends one synthetic group and the default here matches.
 */
export const accessFixture = (overrides = {}) => ({
  isSelf: false,
  permissionOptions: PERMISSION_OPTIONS,
  teams: [{ id: 'all', name: 'All Teams', accounts: [accountFixture()] }],
  ...overrides,
});

export const userFixture = (overrides = {}) => ({
  id: 7,
  fullName: 'Abe Martinez',
  firstName: 'Abe',
  lastName: 'Martinez',
  username: 'm.abe',
  jobRole: '',
  dmsId: '98761234',
  email: 'martinez@email.com',
  mobile: '(350) 579-3153',
  mobileCountryCode: 1,
  phone: '',
  // Null rather than a code: most rows have no number to attach one to, and
  // Management::UserDetail sends the column as it stands.
  phoneCountryCode: null,
  fax: '',
  faxCountryCode: null,
  address1: '',
  address2: '',
  countryCode: 'US',
  state: 'TX',
  city: 'Dallas',
  postalCode: '75001',
  accounts: ['Account Name A'],
  permissions: ['Fleet Administrator'],
  permissionAssignments: [
    { account: 'Account Name A', permission: 'Fleet Administrator' },
  ],
  accountAccess: [
    {
      id: 101,
      decisivId: 'ACCOUNTA',
      name: 'Account Name A',
      dealerType: 'FLEET',
      roles: ['Fleet Administrator'],
      removedAt: null,
    },
    {
      id: 102,
      decisivId: 'ACCOUNTB',
      name: 'Account Name B',
      dealerType: 'MEMBER',
      roles: ['Member'],
      removedAt: '2025-01-01T00:00:00Z',
    },
  ],
  lastLoginAt: '03/08/2025 05:22 PM MST',
  options: OPTIONS,
  ...overrides,
});

export default userFixture;
